import { callApi } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import { MainHeading } from "@/components/common/MainHeading";
import Pagination from "@/components/common/Pagination";
import { StatusBadge } from "@/components/common/StatusBadge";
import { TableFilters } from "@/components/common/TableFilters";
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { TableItemIdentity } from "@/components/common/TableItemIdentity";
import { Layout } from "@/components/Layout/Layout";
import { useFilter } from "@/hooks/useFilter";
import { IPageInfo } from "@/interfaces/common.interface";
import { IReturnOrder, IReturnOrderlistResponse } from "@/interfaces/ReturnOrders/returnOrder.interface";
import { useEffect, useState } from "react";
import { GetFilterUrl, formatDateTime, formatRand, parseOrderAddress, showToastNotification, downloadExcel } from "@/utility/helper";
import { useRoleNavigate } from "@/hooks/useRoleNavigate";
import { RoutesLink } from "@/utility/RouteLinks";
import { Eye, Download } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";


export const Return = () => {
    const navigate = useRoleNavigate();
    const [loading, setLoading] = useState(false);
    const [returnOrderList, setReturnOrderList] = useState<IReturnOrder[]>([]);
    const { canView, canExport } = usePermissions();
    const { filters, debouncedFilters, updateFilter, handlePageChange, handlePageSizeChange } = useFilter({
        search: "",
        page: 1,
        limit: 10,
        startDate: '',
        endDate: '',
    });
    const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0,
    });

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            // status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            // sort: filters.sort
        });
        const url = `${API_URL + API_ENDPOINT.return}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'returns_export.xlsx');
        } catch (error) {

        } finally {
            setLoadingExport(false);
        }
    };

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };
    useEffect(() => {
        getReturnOrders();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search, debouncedFilters.startDate, debouncedFilters.endDate]);

    const getReturnOrders = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IReturnOrderlistResponse>(API_URL + API_ENDPOINT.return + `?${queryParams}`, { method: "GET" });
            setReturnOrderList(response?.data?.returnRequests ?? []);
            setPageMetadata(response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 });
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    const columns: TableColumn<IReturnOrder>[] = [
        {
            key: "order_id",
            header: "Order",
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-primary">#{item.order?.orderId || '---'}</span>
                    <span className="text-[10px] text-muted-foreground">{formatDateTime(item.createdAt)}</span>
                </div>
            ),
        },
        {
            key: "user",
            header: "Customer",
            render: (item) => {
                const parsedAddress = parseOrderAddress(item.order?.address);
                const customerName = parsedAddress?.fullName || '---';
                const phoneNumber = parsedAddress?.phoneNumber || '---';
                return (
                    <div className="flex flex-col">
                        <span className="font-medium text-foreground">{customerName}</span>
                        <span className="text-xs text-muted-foreground">{phoneNumber}</span>
                    </div>
                );
            }
        },
        {
            key: "reason",
            header: "Reason",
            render: (item) => (
                <div className="flex flex-col max-w-[220px]">
                    <span className="font-semibold text-foreground text-xs">{item.reason}</span>
                    {item.customerNotes && (
                        <span className="text-[10px] text-muted-foreground truncate">{item.customerNotes}</span>
                    )}
                </div>
            ),
        },
        {
            key: "refund",
            header: "Refund Amount",
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-foreground">
                        {item.refundAmount ? formatRand(item.refundAmount) : '---'}
                    </span>
                    {item.refundMethod && (
                        <span className="text-[10px] text-muted-foreground uppercase">{item.refundMethod}</span>
                    )}
                </div>
            ),
        },
        {
            key: "status",
            header: "Status",
            render: (item) => <StatusBadge status={item.status} />,
        },
    ];

    return (
        <Layout>
            <MainHeading
                title="Return orders"
                description="Manage all return orders"
            />
            <div className="mt-6 flex flex-col gap-4">
                <TableFilters
                    placeholder="Search Return"
                    searchTerm={filters.search}
                    onSearchChange={(query) => updateFilter({ search: query })}
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.RETURNS) && <Button
                            variant="outline"
                            onClick={() => handleExport()}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl"
                            leftIcon={<Download className="w-4 h-4" />}
                            isLoading={loadingExport}
                        >
                            Export
                        </Button>}
                    </div>
                </TableFilters>
            </div>
            <div className="mt-6 flex flex-col gap-4">
                <CommonTable
                    columns={columns}
                    data={returnOrderList}
                    loading={loading}
                    actions={[
                        ...(canView(RESOURCES.RETURNS) ? [{
                            text: 'View Details',
                            icon: <Eye strokeWidth={1.5} size={16} className="cursor-pointer" />,
                            onClick: (row) => navigate(RoutesLink.ViewReturn.replace(':id', row.id)),
                        }] : []),
                    ]}
                />
                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageMetadata.totalPages}
                    totalItems={pageMetadata.total}
                    itemsPerPage={pageMetadata.limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
        </Layout>
    );
};